/**
 * Invoice Detail Modal
 * Shows complete invoice information with payment history
 */

import React from 'react';
import { X, Download, FileText, Calendar, DollarSign } from 'lucide-react';
import { MonthlyInvoiceDetail } from '../../services/monthlyInvoiceService';
import monthlyInvoiceService from '../../services/monthlyInvoiceService';

interface InvoiceDetailModalProps {
  invoice: MonthlyInvoiceDetail;
  onClose: () => void;
  onUpdate: () => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({ invoice, onClose, onUpdate }) => {
  const handleDownload = async () => {
    try {
      await monthlyInvoiceService.downloadPDF(invoice.id, invoice.numero);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{invoice.numero}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {invoice.assureur.nom} · {monthlyInvoiceService.getMonthName(invoice.month)} {invoice.year}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {invoice.pdf_file && (
              <button
                onClick={handleDownload}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                title="Télécharger PDF"
              >
                <Download className="w-5 h-5" />
              </button>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <FileText className="w-5 h-5" />
                <span className="text-sm font-medium">Factures</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{invoice.number_of_invoices}</div>
              <div className="text-xs text-blue-700 mt-1">{invoice.number_of_patients} patients</div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <DollarSign className="w-5 h-5" />
                <span className="text-sm font-medium">Net à Payer</span>
              </div>
              <div className="text-2xl font-bold text-green-900">
                {monthlyInvoiceService.formatCurrency(invoice.net_to_pay)}
              </div>
              <div className="text-xs text-green-700 mt-1">
                Remise: {monthlyInvoiceService.formatCurrency(invoice.remise_amount)}
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-purple-600 mb-2">
                <DollarSign className="w-5 h-5" />
                <span className="text-sm font-medium">Montant Payé</span>
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {monthlyInvoiceService.formatCurrency(invoice.amount_paid)}
              </div>
              <div className="text-xs text-purple-700 mt-1">
                {(typeof invoice.payment_percentage === 'number'
                  ? invoice.payment_percentage
                  : parseFloat(invoice.payment_percentage || '0')
                ).toFixed(0)}%
              </div>
            </div>
          </div>

          {/* Status & Dates */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Informations</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Statut:</span>
                <span className="ml-2 font-medium text-gray-900">{invoice.status_display}</span>
              </div>
              {invoice.generated_at && (
                <div>
                  <span className="text-gray-600">Généré le:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {new Date(invoice.generated_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
              {invoice.submitted_at && (
                <div>
                  <span className="text-gray-600">Soumis le:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {new Date(invoice.submitted_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
              {invoice.submission_method && (
                <div>
                  <span className="text-gray-600">Méthode:</span>
                  <span className="ml-2 font-medium text-gray-900">{invoice.submission_method}</span>
                </div>
              )}
              {invoice.expected_payment_date && (
                <div>
                  <span className="text-gray-600">Paiement prévu:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {new Date(invoice.expected_payment_date).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              )}
            </div>
            {invoice.notes && (
              <div className="mt-3 pt-3 border-t">
                <span className="text-sm text-gray-600">Notes:</span>
                <p className="text-sm text-gray-900 mt-1">{invoice.notes}</p>
              </div>
            )}
          </div>

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Historique des Paiements</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Montant</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Méthode</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Référence</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Par</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoice.payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(payment.payment_date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">
                          {monthlyInvoiceService.formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{payment.payment_method}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{payment.reference || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{payment.recorded_by_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailModal;
